import { useCallback, useState, useSyncExternalStore } from 'react';
import {
  ChatDraft,
  EMPTY_CHAT_DRAFT,
  getChatDraft,
  patchChatDraft,
  subscribeChatDrafts,
} from './chatDraftsStore';

export type PatchChatDraft = (partial: Partial<ChatDraft>) => void;

/**
 * Reactive access to a single chat thread's message draft.
 *
 * When `threadId` is provided, the draft is backed by the session-scoped draft
 * store (mirrored to sessionStorage). When `threadId` is undefined, drafting is
 * disabled and the value is kept in ephemeral local component state — this keeps
 * the shared ChatInput usable in contexts that do not opt into drafts.
 */
export function useChatDraft(threadId?: string): [ChatDraft, PatchChatDraft] {
  const [localDraft, setLocalDraft] = useState<ChatDraft>(EMPTY_CHAT_DRAFT);

  const storedDraft = useSyncExternalStore(
    subscribeChatDrafts,
    () => (threadId ? getChatDraft(threadId) : EMPTY_CHAT_DRAFT),
    () => EMPTY_CHAT_DRAFT,
  );

  const patch = useCallback<PatchChatDraft>(
    (partial) => {
      if (threadId) {
        patchChatDraft(threadId, partial);
      } else {
        setLocalDraft((prev) => ({ ...prev, ...partial }));
      }
    },
    [threadId],
  );

  return [threadId ? storedDraft : localDraft, patch];
}
